import { ChevronLeft, ChevronRight } from 'lucide-react'
import { describePeriod } from './preferencesText.js'

/** The month `step` months from `period`, `YYYY-MM`. */
export function stepMonth(period: string, step: number): string {
  const [year, month] = period.split('-').map(Number)
  return new Date(Date.UTC(year ?? 1970, (month ?? 1) - 1 + step, 1)).toISOString().slice(0, 7)
}

/**
 * How big the month reads: `sm` is a control among others; `lg` is the anchor of a screen whose every number and
 * decision is about that month (the Month card, Build the month - the user's call, 22 October 2026), so it is the
 * largest text on the card.
 */
export type MonthStepperSize = 'sm' | 'lg'

const LABEL_CLASS: Record<MonthStepperSize, string> = {
  sm: 'text-[11px] font-bold',
  lg: 'text-2xl font-black leading-none sm:text-3xl',
}

const ARROW_CLASS: Record<MonthStepperSize, { button: string; icon: string }> = {
  sm: { button: 'p-1', icon: 'h-4 w-4' },
  lg: { button: 'p-1.5', icon: 'h-6 w-6' },
}

/** An arrow that steps the month shown; disabled at the end of the range, where there is no month to step to. */
function StepButton({ direction, enabled, onStep, size }: { direction: -1 | 1; enabled: boolean; onStep: () => void; size: MonthStepperSize }) {
  const Icon = direction === -1 ? ChevronLeft : ChevronRight
  const label = direction === -1 ? 'The month before' : 'The month after'
  return (
    <button type="button" aria-label={label} title={enabled ? label : 'No month there to show'} disabled={!enabled} onClick={onStep} className={`rounded border border-marine/20 bg-white text-marine transition-colors hover:border-marine disabled:cursor-not-allowed disabled:opacity-30 ${ARROW_CLASS[size].button}`}>
      <Icon className={ARROW_CLASS[size].icon} aria-hidden />
    </button>
  )
}

/**
 * The one way a screen steps between months: arrows either side of the month's name, so stepping reads as turning a page.
 * With `choices` the arrows step through that list (any string, named by `labelOf`); without, a month at a time between
 * `min` and `max` where given.
 */
export function MonthStepper<T extends string = string>({ value, onChange, choices, min, max, labelOf = describePeriod, className = '', size = 'sm' }: { value: T; onChange: (value: T) => void; choices?: readonly T[]; min?: string; max?: string; labelOf?: (value: T) => string; className?: string; size?: MonthStepperSize }) {
  const index = choices ? choices.indexOf(value) : -1
  const target = (direction: -1 | 1): T | null => {
    if (choices) {
      return choices[index + direction] ?? null
    }
    const next = stepMonth(value, direction) as T
    return (min !== undefined && next < min) || (max !== undefined && next > max) ? null : next
  }
  const back = target(-1)
  const forward = target(1)
  return (
    <span className={`inline-flex items-center ${size === 'lg' ? 'gap-2.5' : 'gap-1.5'} ${className}`}>
      <StepButton direction={-1} enabled={back !== null} onStep={() => back !== null && onChange(back)} size={size} />
      <span className={`${LABEL_CLASS[size]} normal-case tracking-normal text-ink`} aria-live="polite">
        {labelOf(value)}
      </span>
      <StepButton direction={1} enabled={forward !== null} onStep={() => forward !== null && onChange(forward)} size={size} />
    </span>
  )
}
